import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '@c2fo/liquidity';
import { downloadAdjustmentInvoicesCsv } from '@/enterprise/data/downloadAdjustmentInvoicesCsv';
import useAdjustmentInvoiceStats from '@/enterprise/data/useAdjustmentInvoiceStats';
import InvoiceDownloadMenu, {
  InvoiceDownloadMenuScope,
} from '@/enterprise/features/invoiceTables/shared/InvoiceDownloadMenu';
import { buildDivisionInvoiceCsvFilename, buildInvoiceCentralCsvFilename } from '@/utils/buildInvoiceCsvFilename';
import { AdjustmentInvoiceSource, AdjustmentInvoiceUrlState } from './types';
import { mapUrlStateToApiSearchFilters } from './utils';

/**
 * Filter-bar download for adjustment invoices.
 * "All" uses the full source taker-markets; "filtered" uses list urlState search/filters.
 * Menu disable uses unfiltered stats so Download all stays available when filters empty the table.
 */
const AdjustmentToolbarActions = ({
  source,
  urlState,
  onDownloadSubmitted,
}: {
  source: AdjustmentInvoiceSource;
  urlState: AdjustmentInvoiceUrlState;
  onDownloadSubmitted?: (payload: { scope: InvoiceDownloadMenuScope }) => void;
}) => {
  const { t } = useTranslation();
  const showSnackbar = useSnackbar();
  const { makerOrganizationUuid, makerOrganizationName, currency, takerMarkets, divisionId } = source;

  const search = useMemo(() => mapUrlStateToApiSearchFilters({ urlState }), [urlState]);

  const { data: unfilteredStats } = useAdjustmentInvoiceStats({
    makerOrganizationUuid,
    takerMarkets,
  });

  const hasRows = (unfilteredStats?.totalCount ?? 0) > 0;

  const sortOptions = {
    sortBy: urlState.sort.key,
    sortDirection: (urlState.sort.direction === 'asc' ? 'ASC' : 'DESC') as 'ASC' | 'DESC',
  };

  const filename =
    divisionId != null
      ? buildDivisionInvoiceCsvFilename({
          makerName: makerOrganizationName,
          divisionId,
          invoiceType: 'adjustments',
        })
      : buildInvoiceCentralCsvFilename({
          makerName: makerOrganizationName,
          currency,
          invoiceType: 'adjustments',
        });

  const handleDownload = async (scope: InvoiceDownloadMenuScope) => {
    if (!hasRows) {
      return;
    }

    onDownloadSubmitted?.({ scope });

    const isFiltered = scope === 'filtered';

    try {
      await downloadAdjustmentInvoicesCsv({
        makerOrganizationUuid,
        takerMarkets,
        ...(isFiltered && search ? { search } : {}),
        options: sortOptions,
        filename,
      });
    } catch {
      showSnackbar({ message: t('core.downloadError') });
    }
  };

  return <InvoiceDownloadMenu disabled={!hasRows} onDownload={handleDownload} />;
};

export default AdjustmentToolbarActions;
